import React, { useState, useEffect } from "react";
import { useSnackbar } from "notistack";
import { useTheme } from "../../contexts/ThemeContext";
import api from "../../services/api";
import CustomModal from "../common/CustomModal";

interface DeviceToken {
  id: number;
  token: string;
  device_name: string;
  description: string | null;
  is_active: boolean;
  last_used_at: string | null;
  last_used_ip: string | null;
  created_at: string;
  created_by: string | null;
  expires_at: string | null;
}

interface CreateTokenData {
  device_name: string;
  description: string;
  days_valid: number | null;
}

const DeviceTokenManagement: React.FC = () => {
  const { isDarkMode } = useTheme();
  const { enqueueSnackbar } = useSnackbar();
  const [tokens, setTokens] = useState<DeviceToken[]>([]);
  const [loading, setLoading] = useState(true);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showTokenModal, setShowTokenModal] = useState(false);
  const [newToken, setNewToken] = useState<any>(null);
  const [selectedToken, setSelectedToken] = useState<DeviceToken | null>(null);
  const [formData, setFormData] = useState<CreateTokenData>({
    device_name: "",
    description: "",
    days_valid: null,
  });

  useEffect(() => {
    loadTokens();
  }, []);

  const loadTokens = async () => {
    try {
      setLoading(true);
      const response: any = await api.get("/device-tokens/");
      setTokens(response.tokens);
    } catch (error) {
      console.error("Error loading device tokens:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleCreateToken = async () => {
    try {
      const params = new URLSearchParams();
      params.append("device_name", formData.device_name);
      if (formData.description) {
        params.append("description", formData.description);
      }
      if (formData.days_valid) {
        params.append("days_valid", formData.days_valid.toString());
      }

      const response: any = await api.post(
        `/device-tokens/create?${params.toString()}`
      );
      console.log("Response from backend:", response);
      console.log("Token object:", response.token);
      setNewToken(response.token);
      setShowTokenModal(true);
      setShowCreateModal(false);
      setFormData({ device_name: "", description: "", days_valid: null });
      loadTokens();
      enqueueSnackbar("Device token created successfully", {
        variant: "success",
      });
    } catch (error) {
      console.error("Error creating token:", error);
      enqueueSnackbar("Error creating device token", { variant: "error" });
    }
  };

  const handleToggleToken = async (tokenId: number) => {
    try {
      await api.put(`/device-tokens/${tokenId}/toggle`);
      loadTokens();
      enqueueSnackbar("Device token status updated", { variant: "success" });
    } catch (error) {
      console.error("Error toggling token:", error);
      enqueueSnackbar("Error toggling device token", { variant: "error" });
    }
  };

  const handleDeleteToken = async (tokenId: number, deviceName: string) => {
    if (
      window.confirm(
        `Are you sure you want to delete the device token for "${deviceName}"?`
      )
    ) {
      try {
        await api.delete(`/device-tokens/${tokenId}`);
        loadTokens();
        enqueueSnackbar("Device token deleted successfully", {
          variant: "success",
        });
      } catch (error) {
        console.error("Error deleting token:", error);
        enqueueSnackbar("Error deleting device token", { variant: "error" });
      }
    }
  };

  const handleViewDetails = async (tokenId: number) => {
    try {
      const response: any = await api.get(`/device-tokens/${tokenId}`);
      setSelectedToken(response);
    } catch (error) {
      console.error("Error loading token details:", error);
    }
  };

  const copyToClipboard = (text: string) => {
    navigator.clipboard.writeText(text);
    enqueueSnackbar("Token copied to clipboard!", { variant: "success" });
  };

  const formatDate = (dateString: string | null) => {
    if (!dateString) return "Never";
    return new Date(dateString).toLocaleString();
  };

  const getStatusColor = (token: DeviceToken) => {
    if (!token.is_active) return "bg-gray-400 text-white";
    if (token.expires_at && new Date(token.expires_at) < new Date())
      return "bg-red-500 text-white";
    return "bg-green-500 text-white";
  };

  const getStatusText = (token: DeviceToken) => {
    if (!token.is_active) return "Inactive";
    if (token.expires_at && new Date(token.expires_at) < new Date())
      return "Expired";
    return "Active";
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <h1
            className={`text-3xl font-bold ${
              isDarkMode ? "text-white" : "text-gray-900"
            }`}
          >
            Device Token Management
          </h1>
          <p
            className={`mt-2 text-sm ${
              isDarkMode ? "text-gray-400" : "text-gray-600"
            }`}
          >
            Manage device tokens that allow specific computers to access the
            system regardless of IP address
          </p>
        </div>
        <button
          onClick={() => setShowCreateModal(true)}
          className="px-6 py-3 bg-phoenix-600 hover:bg-phoenix-700 text-white rounded-lg font-semibold transition-colors shadow-lg flex items-center gap-2"
        >
          <svg
            className="w-5 h-5"
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M12 4v16m8-8H4"
            />
          </svg>
          Create Token
        </button>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <div
          className={`p-6 rounded-lg ${
            isDarkMode ? "bg-gray-800" : "bg-white"
          } shadow`}
        >
          <div className="text-2xl font-bold text-phoenix-600">
            {tokens.length}
          </div>
          <div
            className={`text-sm ${
              isDarkMode ? "text-gray-400" : "text-gray-600"
            }`}
          >
            Total Tokens
          </div>
        </div>
        <div
          className={`p-6 rounded-lg ${
            isDarkMode ? "bg-gray-800" : "bg-white"
          } shadow`}
        >
          <div className="text-2xl font-bold text-green-600">
            {tokens.filter((t) => t.is_active).length}
          </div>
          <div
            className={`text-sm ${
              isDarkMode ? "text-gray-400" : "text-gray-600"
            }`}
          >
            Active Tokens
          </div>
        </div>
        <div
          className={`p-6 rounded-lg ${
            isDarkMode ? "bg-gray-800" : "bg-white"
          } shadow`}
        >
          <div className="text-2xl font-bold text-gray-600">
            {tokens.filter((t) => !t.is_active).length}
          </div>
          <div
            className={`text-sm ${
              isDarkMode ? "text-gray-400" : "text-gray-600"
            }`}
          >
            Inactive Tokens
          </div>
        </div>
        <div
          className={`p-6 rounded-lg ${
            isDarkMode ? "bg-gray-800" : "bg-white"
          } shadow`}
        >
          <div className="text-2xl font-bold text-blue-600">
            {tokens.filter((t) => t.last_used_at).length}
          </div>
          <div
            className={`text-sm ${
              isDarkMode ? "text-gray-400" : "text-gray-600"
            }`}
          >
            Used Tokens
          </div>
        </div>
      </div>

      {/* Tokens List */}
      <div
        className={`rounded-lg shadow overflow-hidden ${
          isDarkMode ? "bg-gray-800" : "bg-white"
        }`}
      >
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
            <thead className={isDarkMode ? "bg-gray-700" : "bg-gray-50"}>
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                  Device Name
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                  Status
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                  Last Used
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                  Last IP
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                  Created
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                  Expires
                </th>
                <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody
              className={`divide-y ${
                isDarkMode ? "divide-gray-700" : "divide-gray-200"
              }`}
            >
              {loading ? (
                <tr>
                  <td colSpan={7} className="px-6 py-12 text-center">
                    <div className="flex justify-center">
                      <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-phoenix-600"></div>
                    </div>
                  </td>
                </tr>
              ) : tokens.length === 0 ? (
                <tr>
                  <td
                    colSpan={7}
                    className={`px-6 py-12 text-center text-sm ${
                      isDarkMode ? "text-gray-400" : "text-gray-500"
                    }`}
                  >
                    No device tokens found. Create one to get started.
                  </td>
                </tr>
              ) : (
                tokens.map((token) => (
                  <tr
                    key={token.id}
                    className={
                      isDarkMode ? "hover:bg-gray-750" : "hover:bg-gray-50"
                    }
                  >
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex items-center">
                        <div className="flex-shrink-0 h-10 w-10 flex items-center justify-center bg-phoenix-100 dark:bg-phoenix-900 rounded-full">
                          <svg
                            className="w-6 h-6 text-phoenix-600"
                            fill="none"
                            viewBox="0 0 24 24"
                            stroke="currentColor"
                          >
                            <path
                              strokeLinecap="round"
                              strokeLinejoin="round"
                              strokeWidth={2}
                              d="M9.75 17L9 20l-1 1h8l-1-1-.75-3M3 13h18M5 17h14a2 2 0 002-2V5a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z"
                            />
                          </svg>
                        </div>
                        <div className="ml-4">
                          <div
                            className={`text-sm font-medium ${
                              isDarkMode ? "text-white" : "text-gray-900"
                            }`}
                          >
                            {token.device_name}
                          </div>
                          {token.description && (
                            <div
                              className={`text-sm ${
                                isDarkMode ? "text-gray-400" : "text-gray-500"
                              }`}
                            >
                              {token.description}
                            </div>
                          )}
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span
                        className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${getStatusColor(
                          token
                        )}`}
                      >
                        {getStatusText(token)}
                      </span>
                    </td>
                    <td
                      className={`px-6 py-4 whitespace-nowrap text-sm ${
                        isDarkMode ? "text-gray-300" : "text-gray-900"
                      }`}
                    >
                      {formatDate(token.last_used_at)}
                    </td>
                    <td
                      className={`px-6 py-4 whitespace-nowrap text-sm ${
                        isDarkMode ? "text-gray-300" : "text-gray-900"
                      }`}
                    >
                      {token.last_used_ip || "-"}
                    </td>
                    <td
                      className={`px-6 py-4 whitespace-nowrap text-sm ${
                        isDarkMode ? "text-gray-300" : "text-gray-900"
                      }`}
                    >
                      {formatDate(token.created_at)}
                    </td>
                    <td
                      className={`px-6 py-4 whitespace-nowrap text-sm ${
                        isDarkMode ? "text-gray-300" : "text-gray-900"
                      }`}
                    >
                      {token.expires_at
                        ? formatDate(token.expires_at)
                        : "Never"}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium space-x-2">
                      <button
                        onClick={() => handleViewDetails(token.id)}
                        className="text-blue-600 hover:text-blue-900 dark:text-blue-400 dark:hover:text-blue-300"
                        title="View Details"
                      >
                        <svg
                          className="w-5 h-5"
                          fill="none"
                          viewBox="0 0 24 24"
                          stroke="currentColor"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={2}
                            d="M15 12a3 3 0 11-6 0 3 3 0 016 0z"
                          />
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={2}
                            d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z"
                          />
                        </svg>
                      </button>
                      <button
                        onClick={() => handleToggleToken(token.id)}
                        className={`${
                          token.is_active
                            ? "text-yellow-600 hover:text-yellow-900 dark:text-yellow-400"
                            : "text-green-600 hover:text-green-900 dark:text-green-400"
                        }`}
                        title={token.is_active ? "Deactivate" : "Activate"}
                      >
                        <svg
                          className="w-5 h-5"
                          fill="none"
                          viewBox="0 0 24 24"
                          stroke="currentColor"
                        >
                          {token.is_active ? (
                            <path
                              strokeLinecap="round"
                              strokeLinejoin="round"
                              strokeWidth={2}
                              d="M18.364 18.364A9 9 0 005.636 5.636m12.728 12.728A9 9 0 015.636 5.636m12.728 12.728L5.636 5.636"
                            />
                          ) : (
                            <path
                              strokeLinecap="round"
                              strokeLinejoin="round"
                              strokeWidth={2}
                              d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"
                            />
                          )}
                        </svg>
                      </button>
                      <button
                        onClick={() =>
                          handleDeleteToken(token.id, token.device_name)
                        }
                        className="text-red-600 hover:text-red-900 dark:text-red-400 dark:hover:text-red-300"
                        title="Delete"
                      >
                        <svg
                          className="w-5 h-5"
                          fill="none"
                          viewBox="0 0 24 24"
                          stroke="currentColor"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={2}
                            d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16"
                          />
                        </svg>
                      </button>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Create Token Modal */}
      {showCreateModal && (
        <CustomModal
          isOpen={showCreateModal}
          onClose={() => setShowCreateModal(false)}
          title="Create Device Token"
        >
          <div className="space-y-4">
            <div>
              <label
                className={`block text-sm font-medium mb-2 ${
                  isDarkMode ? "text-gray-300" : "text-gray-700"
                }`}
              >
                Device Name *
              </label>
              <input
                type="text"
                value={formData.device_name}
                onChange={(e) =>
                  setFormData({ ...formData, device_name: e.target.value })
                }
                className={`w-full px-4 py-2 border rounded-lg focus:outline-none focus:border-phoenix-500 ${
                  isDarkMode
                    ? "bg-gray-700 border-gray-600 text-white"
                    : "bg-white border-gray-300 text-gray-900"
                }`}
                placeholder="e.g., Juan's Laptop"
              />
            </div>

            <div>
              <label
                className={`block text-sm font-medium mb-2 ${
                  isDarkMode ? "text-gray-300" : "text-gray-700"
                }`}
              >
                Description
              </label>
              <textarea
                value={formData.description}
                onChange={(e) =>
                  setFormData({ ...formData, description: e.target.value })
                }
                className={`w-full px-4 py-2 border rounded-lg focus:outline-none focus:border-phoenix-500 ${
                  isDarkMode
                    ? "bg-gray-700 border-gray-600 text-white"
                    : "bg-white border-gray-300 text-gray-900"
                }`}
                rows={3}
                placeholder="Optional description or notes"
              />
            </div>

            <div>
              <label
                className={`block text-sm font-medium mb-2 ${
                  isDarkMode ? "text-gray-300" : "text-gray-700"
                }`}
              >
                Valid for (days)
              </label>
              <input
                type="number"
                value={formData.days_valid || ""}
                onChange={(e) =>
                  setFormData({
                    ...formData,
                    days_valid: e.target.value
                      ? parseInt(e.target.value)
                      : null,
                  })
                }
                className={`w-full px-4 py-2 border rounded-lg focus:outline-none focus:border-phoenix-500 ${
                  isDarkMode
                    ? "bg-gray-700 border-gray-600 text-white"
                    : "bg-white border-gray-300 text-gray-900"
                }`}
                placeholder="Leave empty for no expiration"
              />
              <p
                className={`mt-1 text-xs ${
                  isDarkMode ? "text-gray-400" : "text-gray-500"
                }`}
              >
                Leave empty for tokens that never expire
              </p>
            </div>

            <div className="flex justify-end gap-3 pt-4">
              <button
                onClick={() => setShowCreateModal(false)}
                className={`px-4 py-2 rounded-lg border ${
                  isDarkMode
                    ? "border-gray-600 text-gray-300 hover:bg-gray-700"
                    : "border-gray-300 text-gray-700 hover:bg-gray-50"
                }`}
              >
                Cancel
              </button>
              <button
                onClick={handleCreateToken}
                disabled={!formData.device_name}
                className="px-4 py-2 bg-phoenix-600 hover:bg-phoenix-700 text-white rounded-lg disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Create Token
              </button>
            </div>
          </div>
        </CustomModal>
      )}

      {/* Token Created Modal */}
      {showTokenModal && newToken && (
        <CustomModal
          isOpen={showTokenModal}
          onClose={() => {
            setShowTokenModal(false);
            setNewToken(null);
          }}
          title="Device Token Created"
          maxWidth="xl"
        >
          <div className="space-y-4">
            <div
              className={`p-4 rounded-lg ${
                isDarkMode ? "bg-yellow-900/20" : "bg-yellow-50"
              } border-2 border-yellow-500`}
            >
              <div className="flex items-start gap-3">
                <svg
                  className="w-6 h-6 text-yellow-500 flex-shrink-0 mt-0.5"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"
                  />
                </svg>
                <div>
                  <p className="font-semibold text-yellow-800 dark:text-yellow-200">
                    Important: Save this token now!
                  </p>
                  <p
                    className={`mt-1 text-sm ${
                      isDarkMode ? "text-yellow-300" : "text-yellow-700"
                    }`}
                  >
                    This is the only time you'll see the full token. Copy it and
                    store it securely.
                  </p>
                </div>
              </div>
            </div>

            <div>
              <label
                className={`block text-sm font-medium mb-2 ${
                  isDarkMode ? "text-gray-300" : "text-gray-700"
                }`}
              >
                Device Token
              </label>
              <div className="flex gap-2">
                <input
                  type="text"
                  value={newToken.token}
                  readOnly
                  className={`flex-1 px-4 py-2 border rounded-lg font-mono text-sm ${
                    isDarkMode
                      ? "bg-gray-700 border-gray-600 text-white"
                      : "bg-gray-50 border-gray-300 text-gray-900"
                  }`}
                />
                <button
                  onClick={() => copyToClipboard(newToken.token)}
                  className="px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg flex items-center gap-2"
                >
                  <svg
                    className="w-5 h-5"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z"
                    />
                  </svg>
                  Copy
                </button>
              </div>
            </div>

            <div
              className={`p-4 rounded-lg ${
                isDarkMode ? "bg-gray-700" : "bg-gray-50"
              }`}
            >
              <h4
                className={`font-semibold mb-2 ${
                  isDarkMode ? "text-white" : "text-gray-900"
                }`}
              >
                How to use this token:
              </h4>
              <ol
                className={`list-decimal list-inside space-y-2 text-sm ${
                  isDarkMode ? "text-gray-300" : "text-gray-600"
                }`}
              >
                <li>
                  Install a browser extension like{" "}
                  <a
                    href="https://chrome.google.com/webstore/detail/modheader"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-blue-500 hover:underline"
                  >
                    ModHeader
                  </a>
                </li>
                <li>Add a custom header with name: X-Device-Token</li>
                <li>Paste the token as the value</li>
                <li>Enable it only for board.pxgrouptexas.com</li>
                <li>You can now access from any IP address!</li>
              </ol>
            </div>

            <div className="flex justify-end pt-4">
              <button
                onClick={() => {
                  setShowTokenModal(false);
                  setNewToken(null);
                }}
                className="px-6 py-2 bg-phoenix-600 hover:bg-phoenix-700 text-white rounded-lg"
              >
                I've Saved the Token
              </button>
            </div>
          </div>
        </CustomModal>
      )}

      {/* Token Details Modal */}
      {selectedToken && (
        <CustomModal
          isOpen={!!selectedToken}
          onClose={() => setSelectedToken(null)}
          title="Device Token Details"
          maxWidth="xl"
        >
          <div className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label
                  className={`block text-sm font-medium mb-1 ${
                    isDarkMode ? "text-gray-400" : "text-gray-600"
                  }`}
                >
                  Device Name
                </label>
                <p
                  className={`font-semibold ${
                    isDarkMode ? "text-white" : "text-gray-900"
                  }`}
                >
                  {selectedToken.device_name}
                </p>
              </div>
              <div>
                <label
                  className={`block text-sm font-medium mb-1 ${
                    isDarkMode ? "text-gray-400" : "text-gray-600"
                  }`}
                >
                  Status
                </label>
                <span
                  className={`px-2 py-1 text-xs font-semibold rounded-full ${getStatusColor(
                    selectedToken
                  )}`}
                >
                  {getStatusText(selectedToken)}
                </span>
              </div>
            </div>

            {selectedToken.description && (
              <div>
                <label
                  className={`block text-sm font-medium mb-1 ${
                    isDarkMode ? "text-gray-400" : "text-gray-600"
                  }`}
                >
                  Description
                </label>
                <p
                  className={`${
                    isDarkMode ? "text-gray-300" : "text-gray-700"
                  }`}
                >
                  {selectedToken.description}
                </p>
              </div>
            )}

            <div>
              <label
                className={`block text-sm font-medium mb-1 ${
                  isDarkMode ? "text-gray-400" : "text-gray-600"
                }`}
              >
                Token
              </label>
              <div className="flex gap-2">
                <input
                  type="text"
                  value={selectedToken.token}
                  readOnly
                  className={`flex-1 px-3 py-2 border rounded-lg font-mono text-xs ${
                    isDarkMode
                      ? "bg-gray-700 border-gray-600 text-white"
                      : "bg-gray-50 border-gray-300 text-gray-900"
                  }`}
                />
                <button
                  onClick={() => copyToClipboard(selectedToken.token)}
                  className="px-3 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg text-sm"
                >
                  Copy
                </button>
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <label
                  className={`block text-sm font-medium mb-1 ${
                    isDarkMode ? "text-gray-400" : "text-gray-600"
                  }`}
                >
                  Last Used
                </label>
                <p
                  className={`${
                    isDarkMode ? "text-gray-300" : "text-gray-700"
                  }`}
                >
                  {formatDate(selectedToken.last_used_at)}
                </p>
              </div>
              <div>
                <label
                  className={`block text-sm font-medium mb-1 ${
                    isDarkMode ? "text-gray-400" : "text-gray-600"
                  }`}
                >
                  Last IP
                </label>
                <p
                  className={`font-mono ${
                    isDarkMode ? "text-gray-300" : "text-gray-700"
                  }`}
                >
                  {selectedToken.last_used_ip || "-"}
                </p>
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <label
                  className={`block text-sm font-medium mb-1 ${
                    isDarkMode ? "text-gray-400" : "text-gray-600"
                  }`}
                >
                  Created
                </label>
                <p
                  className={`${
                    isDarkMode ? "text-gray-300" : "text-gray-700"
                  }`}
                >
                  {formatDate(selectedToken.created_at)}
                </p>
              </div>
              <div>
                <label
                  className={`block text-sm font-medium mb-1 ${
                    isDarkMode ? "text-gray-400" : "text-gray-600"
                  }`}
                >
                  Created By
                </label>
                <p
                  className={`${
                    isDarkMode ? "text-gray-300" : "text-gray-700"
                  }`}
                >
                  {selectedToken.created_by || "-"}
                </p>
              </div>
            </div>

            <div>
              <label
                className={`block text-sm font-medium mb-1 ${
                  isDarkMode ? "text-gray-400" : "text-gray-600"
                }`}
              >
                Expires
              </label>
              <p
                className={`${isDarkMode ? "text-gray-300" : "text-gray-700"}`}
              >
                {selectedToken.expires_at
                  ? formatDate(selectedToken.expires_at)
                  : "Never"}
              </p>
            </div>

            <div className="flex justify-end pt-4">
              <button
                onClick={() => setSelectedToken(null)}
                className={`px-4 py-2 rounded-lg border ${
                  isDarkMode
                    ? "border-gray-600 text-gray-300 hover:bg-gray-700"
                    : "border-gray-300 text-gray-700 hover:bg-gray-50"
                }`}
              >
                Close
              </button>
            </div>
          </div>
        </CustomModal>
      )}
    </div>
  );
};

export default DeviceTokenManagement;
